import React, { useEffect, useState } from "react";
import useHoverSfx from "../hooks/useHoverSfx";
import useQuests from "../hooks/useQuests";
import useClaimableQuestCount from "../hooks/useClaimableQuestCount";
import Countdown from "react-countdown";
import Dialog from "./Dialog";
import { motion } from "framer-motion";
import { isMobile } from "react-device-detect";
import { useLocation, useNavigate } from "react-router-dom";
import GrowthChallenge from "./Campaigns/GrowthChallenge";
import { useSelector } from "react-redux";
import { EventBanner, GameAnnouncements } from "../types/global";
import classNames from "classnames";
import useTriggerEvent from "../game-engine/hooks/useTriggerEvent";
import { EventNames } from "../game-engine/utils/event-bus";
import { Badge, OverlayTrigger, Tooltip } from "react-bootstrap";
// .hud-spot-column and the --hud-spot-* tokens it reads live here, with the
// rest of the map HUD.
import "./hud/map-hud.css";
import { useAppTranslation } from "../i18n/useAppTranslation";
type Props = {};

// The map's right-hand column: the Quests button (it opens the Events dialog on
// its Quests tab), then the event banners: the dynamic events the admin
// publishes, each shown as its icon with a countdown to its end.
const IconBoard = (props: Props) => {
  const { t } = useAppTranslation(["common", "navigation"]);
  const { eventBanners }: GameAnnouncements = useSelector(
    (state: any) => state.announcements
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [challengeDialog, setChallengeDialog] = useState(false);
  const [dialogData, setDialogData] = useState("");
  const { hash } = useLocation();
  const navigate = useNavigate();
  const [filteredBanners, setFilteredBanners] = useState<
    Array<EventBanner & { status?: string }>
  >(eventBanners || []);
  const triggerEvent = useTriggerEvent();
  const hoverSfx = useHoverSfx();
  const { getQuests } = useQuests();
  const claimableQuests = useClaimableQuestCount();
  // The Quests button carries the "reward waiting" count. One refresh per map
  // visit keeps it current; the Quests tab refetches whenever it is opened.
  useEffect(() => {
    getQuests();
  }, []);
  useEffect(() => {
    if (hash && dialogData) {
      setIsDialogOpen(false);
    }
  }, [hash]);
  const classifyEventStatus = (startDate: string, endDate: string) => {
    const now = new Date().getTime();
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();

    if (now < start) return "upcoming";
    if (now >= start && now <= end) return "ongoing";
    return "expired";
  };

  // Keeps every active banner, each marked upcoming, ongoing or expired.
  const checkBanners = () => {
    const updatedBanners = eventBanners
      ?.filter((e) => e.isActive)
      ?.map((banner) => ({
        ...banner,
        status: classifyEventStatus(banner.startDate, banner.endDate),
      }));
    setFilteredBanners(updatedBanners);
  };
  useEffect(() => {
    checkBanners();
  }, [eventBanners]);

  const BoardTimer = ({
    date,
    isStarted,
  }: {
    date: Date;
    isStarted: boolean;
  }) => {
    return (
      <div className={"fs-small text-white py-1"}>
        <Countdown
          date={date}
          className={isStarted ? " " : "text-success "}
          onComplete={checkBanners}
        />
      </div>
    );
  };
  return (
    <motion.div
      initial={
        !isMobile
          ? {
              y: -200,
            }
          : undefined
      }
      animate={{ y: 0 }}
      exit={
        !isMobile
          ? {
              y: -200,
            }
          : undefined
      }
      transition={{ delay: 0.2 }}
      // Position/size come from .hud-spot-column in map-hud.css, which sizes
      // the column off viewport media queries. Previously this was an inline
      // style keyed off react-device-detect's `isMobile` (a UA check, true for
      // tablets), which disagreed with the rest of the HUD on iPad.
      className="hud-spot-column d-flex flex-column gap-3 gap-md-4"
    >
      <button type="button" className="hud-spot-quests pointer"
        aria-label={claimableQuests > 0 ? `${t("navigation:quests")} (${claimableQuests})` : t("navigation:quests")}
        onClick={() => navigate({ hash: "#quests" })} {...hoverSfx("topbar")}>
        {claimableQuests > 0 && <Badge bg="danger" className="hud-spot-badge">{claimableQuests}</Badge>}
        <img className="hud-spot-quests-icon" src="/assets/images/icons/leaderboard.png" alt="" />
        <span className="hud-spot-quests-label">{t("navigation:quests")}</span>
      </button>
      {filteredBanners?.map((data, index) => (
        <OverlayTrigger
          key={`event-banner-${data.id || index}`}
          placement="left"
          overlay={
            <Tooltip id={`tooltip-${data.id || index}`}>
              {data.description || data.title || t("common:events.event")}
            </Tooltip>
          }
        >
          <div
            className={classNames("pointer icon-spot", {
              "button-disabled": data.status === "expired",
            })}
            {...hoverSfx("topbar", index)}
            onClick={() => {
              if (data.popupId) {
                triggerEvent(EventNames.OpenPopup, { id: data.popupId });
                return;
              }
              if (data.html && !data.link) {
                setIsDialogOpen(true);
                setDialogData(data.html);
              }
              if (data.link) {
                if (data?.link?.includes("http")) {
                  window.open(data.link, "_blank");
                  return;
                }
                navigate(data.link);
              }
            }}
          >
            {data.iconUrl?.includes(".webm") ? (
              <video
                controls={false}
                className="icon-spot-media"
                src={data.iconUrl}
                loop
                autoPlay
              />
            ) : (
              <img
                className="icon-spot-media"
                src={data.iconUrl}
                alt={data.title}
              />
            )}
            <div className="icon-spot-label">
              {data?.endDate && new Date(data.endDate) > new Date() && (
                <BoardTimer
                  isStarted={data.status === "ongoing"}
                  date={new Date(data.endDate)}
                />
              )}
            </div>
          </div>
        </OverlayTrigger>
      ))}
      <Dialog
        size="lg"
        title={t("common:events.information")}
        open={isDialogOpen}
        submitLabel={t("common:events.startNow")}
        onSubmit={() => {
          setIsDialogOpen(false);
        }}
        noFooter={true}
        onClose={() => setIsDialogOpen(false)}
      >
        <div
          style={{ marginBottom: "-30px" }}
          dangerouslySetInnerHTML={{ __html: dialogData }}
        />
      </Dialog>
      <GrowthChallenge
        open={challengeDialog}
        onClose={() => setChallengeDialog(false)}
      />
    </motion.div>
  );
};

export default IconBoard;
